<script setup lang="ts">
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import UserForm from '@/views/users/UserForm.vue'
import { usersApi, type UserPayload } from '@/services/users'

/**
 * Alta de un usuario.
 *
 * Los errores del servidor se mantienen por campo y se leen de
 * `error.details.fields`, no del formato de validación de Laravel: el envelope de
 * la API lo envuelve en `error` y las aserciones que lo comprueban deben apuntar
 * ahí. Un formulario que solo mirara el primer error mostraría "no se pudo crear"
 * cuando el problema es que el usuario ya existe.
 */

const router = useRouter()

const submitting = ref(false)
const serverErrors = ref<Record<string, string[]>>({})

const model = ref({
  username: '',
  name: '',
  email: '',
  phone: '',
  attention_line: '',
  active: true,
  roles: ['advisor'] as string[],
  password: '',
  password_confirmation: '',
})

/**
 * Volver al listado **con los filtros con los que se llegó**.
 *
 * El listado lleva sus filtros en la URL —para eso está `useFiltrosEnUrl`—, así que al
 * abrir esta vista se los lleva en la query. Sin devolvérselos, quien filtra, abre una
 * ficha y vuelve se encuentra la lista entera: el filtro se aplicó y desapareció.
 *
 * «Volver» empuja una entrada **nueva**, así que el historial no salva: la entrada anterior,
 * la que tenía los filtros, deja de ser la que se está usando.
 */
const { destino: destinoListado, volver: volverAlListado } = useVolverAlListado('users.index')

function extractFields(cause: unknown): Record<string, string[]> {
  const maybeAxios = cause as { response?: { data?: { error?: { details?: { fields?: Record<string, string[]> } } } } }
  return maybeAxios.response?.data?.error?.details?.fields ?? {}
}

function extractMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo crear el usuario. Revisa tu conexión e inténtalo de nuevo.'
}

async function onSubmit(payload: UserPayload): Promise<void> {
  submitting.value = true
  serverErrors.value = {}

  try {
    await usersApi.create(payload)
    await router.push(destinoListado.value)
  } catch (cause: unknown) {
    serverErrors.value = { ...extractFields(cause), general: [extractMessage(cause)] }
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header>
      <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
        Nuevo usuario
      </h1>
      <p class="mt-1 text-surface-500 dark:text-surface-400">
        La cuenta podrá entrar al CRM en cuanto se guarde
      </p>
    </header>

    <AppCard>
      <UserForm
        v-model="model"
        :submitting="submitting"
        :server-errors="serverErrors"
        :is-edit="false"
        @submit="onSubmit"
        @cancel="volverAlListado"
      />
    </AppCard>
  </div>
</template>
